import type { ShareableBasicCollection } from '@ilteducation/content-collections-types';
import type { CollectionContentType, MemberRole } from '@ilteducation/content-collections-types';

export type Page<T> = {
  data: T[];
  cursor: string | null;
};

export type CollectionsByIdsSelectorConfig = {
  type: 'CollectionsByIds';
  ids: string[];
};

export type HistoryCollectionSelectorConfig = {
  type: 'HistoryCollectionSelector';
  contentType?: CollectionContentType; // TODO change to using a "context" later instead of content type...
};

export type FavouritesCollectionSelectorConfig = {
  type: 'FavouritesCollectionSelector';
  context: string;
};

export type CollectionsByMembershipSelectorConfig = {
  type: 'CollectionsByMembershipSelector';
  role: MemberRole;
  context: string;
};

export type ContentSharedCollectionSelectorConfig = {
  type: 'ContentSharedCollectionSelector';
  context: string;
};

export type CollectionsSelectorConfig =
  | CollectionsByIdsSelectorConfig
  | HistoryCollectionSelectorConfig
  | FavouritesCollectionSelectorConfig
  | CollectionsByMembershipSelectorConfig
  | ContentSharedCollectionSelectorConfig;

export type ContentSection = {
  type: 'Content';
  id: string;
  // In V2, a section contains an ordered list of content items
  content: (Link | CollectionsSelectorConfig | Text)[];
};

export type SelectorSection = {
  type?: 'Selector';
  id: string;
  selectors: CollectionsSelectorConfig[];
};

export type ViewSectionConfiguration = ContentSection | SelectorSection;

export type View = {
  id: string;
  realm: string;
  key: string; // ex. polyglutt-preschool
  type?: 'Selector' | 'Content';  // if type is missing its Selector
  sections: ViewSectionConfiguration[];
};

export const isSelectorSection = (section: any): section is SelectorSection => section.selectors;
export const isContentSection = (section: any): section is ContentSection => section.type === 'Content';

export const isSelectorSections = (sections: any): sections is SelectorSection[] => sections.every((section: any) => isSelectorSection(section));
export const isContentSections = (sections: any): sections is ContentSection[] => sections.every((section: any) => isContentSection(section));

export type ViewPatch = Pick<View, 'sections'>;

export type ResolvedViewSection = {
  id: string;
  collections: Page<ShareableBasicCollection>;
};

// View V2 supports display configuration, this defines what type of content it is and config for how to display it.

// Display for Link items
export type BoxDisplayConfiguration = {
  type: 'Box';
  title: string;
  description?: string;
  color: string;
};

export type ImageDisplayConfiguration = {
  type: 'Image';
  imageId: string;
  title: string;
  description?: string;
};

export type Link = {
  type: 'Link';
  name: string;
  target: ChildView;
  displayConfiguration?: BoxDisplayConfiguration | ImageDisplayConfiguration;
};

export type Text = {
  type: 'Text';
  name: string;
  text: string;
};

export type ChildView = {
  name: string;
  sections: ContentSection[];
};
